import React, { useEffect, useState } from 'react';
import { CheckCircle2, Circle, Smartphone, X, Share, MoreVertical, Loader2, Bell } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import {
  isPushSupported,
  isStandalonePWA,
  getCurrentPermission,
  getActiveSubscription,
  subscribeToPush,
} from '../lib/push';

const STORAGE_KEY = 'rxgrit_setup_dismissed_v1';

const detectPlatform = () => {
  if (typeof window === 'undefined') return 'desktop';
  const ua = navigator.userAgent || '';
  if (/iPhone|iPad|iPod/i.test(ua)) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  return 'desktop';
};

/**
 * Self-hiding "Quick Setup" card. Shows on the user dashboard only when
 * the user hasn't yet completed both steps (install + push). Auto-hides
 * the moment both are done. Dismissible via the × (stored in localStorage).
 */
const QuickSetupCard = () => {
  const [platform] = useState(() => detectPlatform());
  const [supported] = useState(() => isPushSupported());
  const [installed, setInstalled] = useState(() => isStandalonePWA());
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(STORAGE_KEY) === '1';
  });

  // Refresh the "are we in standalone mode?" check periodically and on
  // display-mode change. iOS doesn't fire any reliable event when the user
  // adds to Home Screen, so we poll.
  useEffect(() => {
    const mq = window.matchMedia ? window.matchMedia('(display-mode: standalone)') : null;
    const onChange = () => setInstalled(isStandalonePWA());
    if (mq && mq.addEventListener) mq.addEventListener('change', onChange);
    const id = setInterval(onChange, 4000);
    return () => {
      if (mq && mq.removeEventListener) mq.removeEventListener('change', onChange);
      clearInterval(id);
    };
  }, []);

  // Check current subscription state when in PWA mode
  useEffect(() => {
    if (!supported || !installed) {
      setSubscribed(false);
      return;
    }
    (async () => {
      const sub = await getActiveSubscription();
      setSubscribed(!!sub);
    })();
  }, [supported, installed]);

  const handleEnablePush = async () => {
    setBusy(true);
    try {
      await subscribeToPush();
      setSubscribed(true);
      toast.success('All set! You\'ll see a red dot on your icon for new updates.');
    } catch (err) {
      const perm = getCurrentPermission();
      if (perm === 'denied') {
        toast.error('Notifications were blocked. Open iOS Settings → Notifications → RxGRIT to enable.');
      } else {
        toast.error(err?.message || 'Could not enable notifications. Try again.');
      }
    } finally {
      setBusy(false);
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    try { window.localStorage.setItem(STORAGE_KEY, '1'); } catch { /* localStorage unavailable - non-blocking */ }
  };

  // Hide whenever the user has nothing left to do, or has dismissed the card.
  // NOTE: We intentionally still render on mobile browsers where PushManager
  // isn't available yet (e.g. iOS Safari before "Add to Home Screen") because
  // Step 1 — install — is precisely how the user unlocks push support. On
  // desktop without push support there's nothing actionable, so we bail.
  if (dismissed) return null;
  if (installed && subscribed) return null;
  if (installed && !supported) return null;
  if (!supported && platform === 'desktop') return null;

  const step1Done = installed;
  const step2Done = installed && subscribed;

  return (
    <div
      className="bg-white/60 border border-[#EFB342]/40 rounded-2xl p-4 sm:p-5 relative"
      data-testid="quick-setup-card"
    >
      <button
        onClick={handleDismiss}
        className="absolute top-3 right-3 text-stone-500 hover:text-stone-700 transition"
        aria-label="Dismiss setup"
        data-testid="quick-setup-dismiss"
      >
        <X size={18} />
      </button>

      <div className="flex items-start gap-3 mb-3 pr-7">
        <div className="w-10 h-10 bg-[#EFB342]/15 rounded-xl flex items-center justify-center shrink-0">
          <Smartphone className="text-[#EFB342]" size={20} />
        </div>
        <div>
          <p className="text-base font-semibold text-stone-900">📱 Quick Setup</p>
          <p className="text-xs text-stone-600 mt-0.5">
            Don&apos;t miss the red dot on your icon when there&apos;s a new community message. Two quick steps:
          </p>
        </div>
      </div>

      {/* Step 1 — Install */}
      <div className="flex items-start gap-3 mb-3" data-testid="quick-setup-step-1">
        {step1Done ? (
          <CheckCircle2 className="text-emerald-500 shrink-0 mt-0.5" size={20} />
        ) : (
          <Circle className="text-stone-400 shrink-0 mt-0.5" size={20} />
        )}
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-medium ${step1Done ? 'text-stone-600 line-through' : 'text-stone-900'}`}>
            Add RxGRIT to your home screen
          </p>
          {!step1Done && (
            <p className="text-xs text-stone-600 mt-1 flex items-center gap-2 flex-wrap">
              {platform === 'ios' && (
                <>
                  In Safari, tap <Share size={14} className="inline text-[#EFB342]" /> Share → <strong className="text-stone-900">&quot;Add to Home Screen&quot;</strong>
                </>
              )}
              {platform === 'android' && (
                <>
                  In Chrome, tap <MoreVertical size={14} className="inline text-[#EFB342]" /> Menu → <strong className="text-stone-900">&quot;Add to Home Screen&quot;</strong>
                </>
              )}
              {platform === 'desktop' && (
                <>Open this site on your phone, then tap Share → <strong className="text-stone-900">&quot;Add to Home Screen&quot;</strong></>
              )}
            </p>
          )}
        </div>
      </div>

      {/* Step 2 — Enable Push */}
      <div className="flex items-start gap-3" data-testid="quick-setup-step-2">
        {step2Done ? (
          <CheckCircle2 className="text-emerald-500 shrink-0 mt-0.5" size={20} />
        ) : (
          <Circle className={`shrink-0 mt-0.5 ${step1Done ? 'text-[#EFB342]' : 'text-stone-300'}`} size={20} />
        )}
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-medium ${step2Done ? 'text-stone-600 line-through' : step1Done ? 'text-stone-900' : 'text-stone-500'}`}>
            Turn on home-screen alerts
          </p>
          {step1Done && !step2Done && supported && (
            <div className="mt-2">
              <Button
                onClick={handleEnablePush}
                disabled={busy}
                className="bg-[#EFB342] text-black hover:bg-[#D99F30] h-9 text-sm"
                data-testid="quick-setup-enable-push-btn"
              >
                {busy ? <Loader2 className="animate-spin mr-2" size={14} /> : <Bell size={14} className="mr-2" />}
                Enable home-screen notifications
              </Button>
            </div>
          )}
          {step1Done && !supported && (
            <p className="text-xs text-stone-500 mt-1">
              Open RxGRIT from the icon you just added to your home screen, then come back here to turn on alerts.
            </p>
          )}
          {!step1Done && (
            <p className="text-xs text-stone-400 mt-1">Available once you&apos;ve done step 1</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuickSetupCard;
